import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Tooltip,
} from '@mui/material';

import type { ScenarioPerformance } from '../../types/models/scenarioPerformance';
import { Column, DataTable } from '../../components/ShootingData/DataTable';
import { useScenarioPerformance } from '../../hooks/useScenarios';
import { EnhancedSession } from '../../types/ui/userSession';
import PageBreadcrumbs from '../Shared/PageBreadcrumbs';
import { formatDuration, formatHitFactor, formatShotScore } from '../../utils/formatters';
import { UserStatistics } from '../../types/ui/userStatistics';

interface LocationState {
    session?: EnhancedSession;
    stats?: UserStatistics;
}

const SessionDetail: React.FC = () => {
    const { sessionId } = useParams<{ sessionId: string }>();
    const location = useLocation();
    const navigate = useNavigate();

    const {
        data: scenarios = [],
        isLoading,
        error,
    } = useScenarioPerformance(Number(sessionId));

    const [session, setSession] = React.useState<EnhancedSession | undefined>(() => {
        return (location.state as LocationState)?.session;
    });
    const stats = (location.state as LocationState)?.stats;

    React.useEffect(() => {
        const state = location.state as LocationState;
        if (state?.session) {
            setSession(state.session);
        }
    }, [location.state]);

    if (!session) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography color="error">Session information is missing.</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 4, backgroundColor: '#0D1117', minHeight: '100vh' }}>
                <Alert severity="error">{error.message}</Alert>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    const paddedId = session.id?.toString().padStart(2, '0');

    const scenarioColumns: Column<ScenarioPerformance>[] = [
        { header: 'Scenarios', accessor: r => r.scenario, align: 'left' },
        { header: 'Duration', accessor: r => formatDuration(r.averageSpeed), align: 'center' },
        { header: 'Hit Factor', accessor: r => formatHitFactor(r.hit_factor), align: 'center' },
        {
            header: 'Hit Score',
            accessor: (row) => {
                const normalize = (str: string) => str.replace(/\s+/g, '').toLowerCase();

                const match = stats?.scenarios.find(
                    s => normalize(s.name) === normalize(row.scenario)
                );
                const best = match?.bestHitFactor;
                return (
                    <Tooltip
                        title={
                            <Typography sx={{ fontSize: '0.9rem', px: 1 }}>
                                Best: {formatHitFactor(best)}
                            </Typography>
                        }
                        arrow
                        disableHoverListener={best === undefined}
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    bgcolor: 'rgba(29, 111, 120, 0.5)', // dark background
                                    color: 'white',
                                    borderRadius: 2,
                                    fontSize: '0.5rem',
                                    p: 1,
                                },
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-block',
                                cursor: best !== undefined ? 'help' : 'default',
                                width: '100%',
                            }}
                        >
                            {formatShotScore(row.averageScore)}
                        </Box>
                    </Tooltip>

                );
            },
            align: 'center',
        }


    ];

    return (
        <Box
            key={location.key}
            sx={{ p: 4, backgroundColor: 'rgba(14,23,32,0)', minHeight: '100vh' }}
        >
            <PageBreadcrumbs
                items={[
                    {
                        label: `${session.dateLabel}`,
                        to: {
                            pathname: `/client-admin/shooting-data/${session.id}`,
                            state: { session, stats },
                        },
                    },
                ]}
            />

            <Box sx={{ p: 4, backgroundColor: 'rgba(14,23,32,0)', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ ml: 1 }}>
                        <Typography variant="h4" sx={{ color: '#FFF' }}>
                            {(session as any).displayDateLabel ?? session.dateLabel}
                        </Typography>
                        <Typography variant="h5" sx={{ color: '#FFF' }}>
                            {session.timeLabel}
                        </Typography>
                    </Box>
                </Box>

                {scenarios.length > 0 ? (
                    <DataTable<ScenarioPerformance>
                        title=""
                        data={scenarios}
                        isLoading={false}
                        error={undefined}
                        columns={scenarioColumns}
                        onRowClick={(row) =>
                            navigate(`scenario/${row.userTransactionId}/repetitions`, {
                                state: {
                                    session,
                                    scenario: row,
                                    stats,
                                },
                            })
                        }
                    />
                ) : (
                    <Typography sx={{ color: 'gray', textAlign: 'center', mt: 4 }}>
                        Trend data unavailable
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default SessionDetail;
